import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import { BLOG_POSTS } from "@/data/blog";

export default function BlogIndex() {
  useEffect(() => {
    document.title =
      "Blog & Insights | Custom Clearance Partner | India Customs Clearance for Freight Forwarders";
    // canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://customclearancepartner.in/blog";

    // Blog schema
    const id = "ccp-blog-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Custom Clearance Partner — Insights",
      url: "https://customclearancepartner.in/blog",
      publisher: { "@id": "https://customclearancepartner.in/#org" },
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://customclearancepartner.in/blog/${p.slug}`,
        datePublished: p.dateISO,
        author: { "@type": "Person", name: "Jaisy Atharth" },
      })),
    });
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, []);

  return (
    <div className="grain min-h-screen bg-[#F9FAFB] text-[#101828]" data-testid="blog-index-page">
      <Header />
      <main className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <Link
            to="/"
            data-testid="blog-back-home"
            className="font-mono-accent inline-flex items-center gap-2 text-[#475467] hover:text-[#D92D20]"
          >
            <ArrowLeft className="h-3 w-3" strokeWidth={3} /> BACK TO HOME
          </Link>
          <p className="overline mt-8 mb-4">§ Insights</p>
          <h1 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-[#101828] sm:text-5xl md:text-6xl">
            India customs, <span className="text-[#D92D20]">field notes.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#475467]">
            Long-form operating knowledge from our desk — the material we wish
            existed when we first built an India customs practice. Written by
            practitioners, updated when the regulations change.
          </p>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BLOG_POSTS.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="bento-card group flex h-full flex-col justify-between border-2 border-[#101828] bg-[#F9FAFB] p-6 hover:border-[#D92D20]"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono-accent text-[#D92D20]">{p.tag}</span>
                    <span className="font-mono-accent text-[10px] text-[#475467]">
                      {p.dateDisplay}
                    </span>
                  </div>
                  <h2 className="mt-4 font-display text-xl font-bold leading-tight tracking-tight text-[#101828]">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-[#475467]">
                    {p.description}
                  </p>
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-[#E4E7EC] pt-4">
                  <span className="font-mono-accent text-[#475467]">{p.readTime}</span>
                  <ArrowUpRight
                    className="h-5 w-5 text-[#101828] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    strokeWidth={2.5}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
